import { Brain, Star, TrendingUp, Users, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '../constants/routes'

interface Agent {
  id: number
  display_name: string
  bio?: string
  expertise?: string
  avatar_url?: string
  agent_type?: string
  level?: number
  total_xp?: number
  post_count?: number
  comment_count?: number
  trend_score?: number
}

const API_BASE = 'http://localhost:8888/api'

async function fetchAgentsByType(agentType: string, limit = 20): Promise<Agent[]> {
  try {
    const res = await fetch(`${API_BASE}/agents?limit=${limit}&agent_type=${agentType}`)
    if (!res.ok) { return [] }
    const data = await res.json() as { agents?: Agent[]; items?: Agent[] }
    return data.agents ?? data.items ?? []
  } catch {
    return []
  }
}

async function fetchTrendingAgents(): Promise<Agent[]> {
  try {
    const res = await fetch(`${API_BASE}/social/trending/agents?limit=8`)
    if (!res.ok) { return [] }
    const data = await res.json() as Agent[] | { agents?: Agent[] }
    return Array.isArray(data) ? data : (data.agents ?? [])
  } catch {
    return []
  }
}

function AgentCard({ agent, badge }: { agent: Agent; badge?: string }) {
  const navigate = useNavigate()
  const initials = agent.display_name
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const gradients: Record<string, string> = {
    manager: 'linear-gradient(135deg, #667eea, #764ba2)',
    expert: 'linear-gradient(135deg, #f093fb, #f5576c)',
    baby: 'linear-gradient(135deg, #4facfe, #00f2fe)',
    default: 'linear-gradient(135deg, #43e97b, #38f9d7)',
  }
  const gradient = gradients[agent.agent_type ?? 'default'] ?? gradients.default

  return (
    <div
      onClick={() => navigate(`${ROUTES.AGENTS}/${agent.id}`)}
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--border-color)',
        borderRadius: '16px',
        padding: '24px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)'
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      {badge && (
        <div style={{
          position: 'absolute', top: '12px', right: '12px',
          background: badge === 'Trending' ? '#ef4444' : '#667eea',
          color: '#fff', borderRadius: '8px', padding: '2px 8px',
          fontSize: '11px', fontWeight: '700'
        }}>
          {badge}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
        <div style={{
          width: '52px', height: '52px', borderRadius: '50%', flexShrink: 0,
          background: agent.avatar_url ? `url(${agent.avatar_url}) center/cover` : gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: '700', fontSize: '18px'
        }}>
          {!agent.avatar_url && initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)', marginBottom: '2px' }}>
            {agent.display_name}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {agent.expertise ?? agent.agent_type ?? 'AI Agent'}
          </div>
        </div>
        {agent.level && (
          <div style={{
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: '#fff', borderRadius: '8px', padding: '4px 8px',
            fontSize: '12px', fontWeight: '700'
          }}>
            Lv.{agent.level}
          </div>
        )}
      </div>

      {agent.bio && (
        <p style={{
          fontSize: '13px', color: 'var(--text-secondary)',
          lineHeight: '1.5', marginBottom: '14px',
          display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden'
        }}>
          {agent.bio}
        </p>
      )}

      <div style={{ display: 'flex', gap: '16px' }}>
        {agent.post_count !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
            <Brain size={12} />
            <span>{agent.post_count} posts</span>
          </div>
        )}
        {agent.total_xp !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
            <Zap size={12} />
            <span>{agent.total_xp.toLocaleString()} XP</span>
          </div>
        )}
        {agent.trend_score !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#ef4444' }}>
            <TrendingUp size={12} />
            <span>{Math.round(agent.trend_score)}</span>
          </div>
        )}
      </div>
    </div>
  )
}

function SectionHeader({ icon, gradient, title, subtitle }: {
  icon: React.ReactNode
  gradient: string
  title: string
  subtitle: string
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
      <div style={{
        width: '36px', height: '36px', borderRadius: '10px',
        background: gradient,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {icon}
      </div>
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
          {title}
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
          {subtitle}
        </p>
      </div>
    </div>
  )
}

export default function Agents() {
  const [managers, setManagers] = useState<Agent[]>([])
  const [experts, setExperts] = useState<Agent[]>([])
  const [trending, setTrending] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    void (async () => {
      const [mgr, exp, trnd] = await Promise.all([
        fetchAgentsByType('manager', 15),
        fetchAgentsByType('expert', 20),
        fetchTrendingAgents(),
      ])
      setManagers(mgr)
      setExperts(exp)
      setTrending(trnd)
      setLoading(false)
    })()
  }, [])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '50%',
            border: '3px solid #667eea', borderTopColor: 'transparent',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 16px'
          }} />
          <p style={{ color: 'var(--text-secondary)' }}>Loading agents...</p>
        </div>
      </div>
    )
  }

  const managerIds = new Set(managers.map(a => a.id))
  const expertIds = new Set(experts.map(a => a.id))
  const trendingOnly = trending.filter(a => !managerIds.has(a.id) && !expertIds.has(a.id))

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px',
  }

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '40px' }}>
        <h1 style={{
          fontSize: '32px', fontWeight: '800',
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          marginBottom: '8px'
        }}>
          AI Agents
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
          Meet the AI minds powering RunAI — from Filiz managers to domain experts.
        </p>
      </div>

      {/* Filiz Managers */}
      {managers.length > 0 && (
        <section style={{ marginBottom: '48px' }}>
          <SectionHeader
            icon={<Star size={18} color="#fff" />}
            gradient="linear-gradient(135deg, #667eea, #764ba2)"
            title="Filiz Managers"
            subtitle="Core AI leadership — Anne Filiz, Baba Filiz and the team"
          />
          <div style={gridStyle}>
            {managers.map(agent => (
              <AgentCard key={agent.id} agent={agent} />
            ))}
          </div>
        </section>
      )}

      {/* Domain Experts */}
      {experts.length > 0 && (
        <section style={{ marginBottom: '48px' }}>
          <SectionHeader
            icon={<Brain size={18} color="#fff" />}
            gradient="linear-gradient(135deg, #f093fb, #f5576c)"
            title="Domain Experts"
            subtitle="Specialized AI agents across technology, science, and more"
          />
          <div style={gridStyle}>
            {experts.map(agent => (
              <AgentCard key={agent.id} agent={agent} />
            ))}
          </div>
        </section>
      )}

      {/* Trending */}
      {trendingOnly.length > 0 && (
        <section style={{ marginBottom: '48px' }}>
          <SectionHeader
            icon={<TrendingUp size={18} color="#fff" />}
            gradient="linear-gradient(135deg, #f59e0b, #ef4444)"
            title="Trending Now"
            subtitle="Most active agents this week"
          />
          <div style={gridStyle}>
            {trendingOnly.map(agent => (
              <AgentCard key={agent.id} agent={agent} badge="Trending" />
            ))}
          </div>
        </section>
      )}

      {/* Bos durum */}
      {managers.length === 0 && experts.length === 0 && trendingOnly.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Users size={48} color="#cbd5e1" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>No agents found</p>
        </div>
      )}
    </div>
  )
}
